import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Download as DownloadIcon } from "lucide-react";
import { api, errMsg, inr, fmtDate, download } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Pill } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function Batches({ reload }) {
  const { can } = useApp();
  const [b, setB] = useState([]);
  const load = () => api.get("/export/batches").then((r) => setB(r.data));
  useEffect(() => { load(); }, [reload]);
  const mark = async (id, status) => {
    const note = status === "import_failed" ? window.prompt("What did Tally report? (entries will be unlocked for re-export)") : "Confirmed in TallyPrime";
    if (note === null) return;
    try { await api.post(`/export/batches/${id}/status`, { status, note }); toast.success("Batch status updated"); load(); } catch (e) { toast.error(await errMsg(e)); }
  };
  return (
    <div className="panel overflow-x-auto">
      <table className="w-full min-w-[900px] text-sm">
        <thead className="text-left text-xs text-muted-foreground"><tr>{["Batch ID", "Created", "By", "Format", "Entries", "Amount", "Status", ""].map((h) => <th key={h} className="p-3 font-medium">{h}</th>)}</tr></thead>
        <tbody>{b.map((x) => (
          <tr key={x.id} className="border-t" data-testid="export-batch-row">
            <td className="num p-3 text-xs">{x.id}{x.reexport && <div className="text-[10px] text-amber-500">re-export: {x.reexport_reason}</div>}</td>
            <td className="num p-3 text-xs">{x.created_at.slice(0, 16).replace("T", " ")}</td><td className="p-3 text-xs">{x.created_by}</td>
            <td className="p-3 uppercase">{x.format}</td><td className="num p-3">{x.count}</td><td className="num p-3">{inr(x.amount)}</td>
            <td className="p-3"><Pill s={x.status} testid="export-batch-status" /></td>
            <td className="flex gap-2 p-3">
              <Button size="sm" variant="ghost" onClick={() => download(`/export/batches/${x.id}/download`)} data-testid="batch-download-button"><DownloadIcon className="h-4 w-4" /></Button>
              {x.status === "exported" && can("admin", "accountant") && <>
                <Button size="sm" variant="outline" onClick={() => mark(x.id, "imported")} data-testid="mark-imported-button">Mark imported into Tally</Button>
                <Button size="sm" variant="ghost" onClick={() => mark(x.id, "import_failed")} data-testid="mark-failed-button">Import failed</Button></>}
            </td>
          </tr>))}</tbody>
      </table>
      {b.length === 0 && <div className="p-6 text-sm text-muted-foreground">No export batches yet.</div>}
    </div>
  );
}

export default function Export() {
  const { can } = useApp();
  const [format, setFormat] = useState("xml");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const [reason, setReason] = useState("");
  const dry = async () => {
    setBusy(true);
    try { const r = await api.post("/export/dry-run", { format }); setRes(r.data); } catch (e) { toast.error(await errMsg(e)); }
    setBusy(false);
  };
  const exp = async () => {
    setBusy(true);
    try {
      await download("/export", { method: "post", data: { ids: res.rows.filter((r) => !r.errors.length).map((r) => r.id), format, reexport_reason: reason } });
      toast.success("Exported. Import into TallyPrime, then mark the batch as imported.");
      setRes(null); setTick((t) => t + 1);
    } catch (e) { toast.error(await errMsg(e)); }
    setBusy(false);
  };
  const invalid = res?.rows.filter((r) => r.errors.length) || [];
  return (
    <div>
      <PageHeader title="TallyPrime Export" sub="Only approved entries from verified statements are exported. Dry-run validates ledgers, voucher types, Dr/Cr balance, bill allocation, financial year and duplicate-posting risk.">
        <Select value={format} onValueChange={setFormat}>
          <SelectTrigger className="w-48" data-testid="export-format-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="xml">Tally XML</SelectItem><SelectItem value="xlsx">Excel</SelectItem><SelectItem value="csv">CSV</SelectItem><SelectItem value="json">JSON</SelectItem></SelectContent>
        </Select>
        <Button variant="outline" onClick={dry} disabled={busy} data-testid="tally-export-dry-run-button"><ShieldCheck className="mr-2 h-4 w-4" />Dry-run validation</Button>
      </PageHeader>
      {res && (
        <div className="mb-10 fade-up">
          <div className="mb-4 grid grid-cols-2 gap-4 md:grid-cols-4">
            <div className="panel p-4"><div className="label-xs">Candidates</div><div className="num mt-1 text-2xl">{res.total}</div></div>
            <div className="panel p-4"><div className="label-xs">Valid</div><div className="num mt-1 text-2xl text-emerald-500" data-testid="dry-run-valid-count">{res.valid}</div></div>
            <div className="panel p-4"><div className="label-xs">Blocked</div><div className="num mt-1 text-2xl text-rose-500" data-testid="dry-run-invalid-count">{res.invalid}</div></div>
            <div className="panel p-4"><div className="label-xs">Valid amount</div><div className="num mt-1 text-xl">{inr(res.valid_amount)}</div></div>
          </div>
          {res.total === 0 && <div className="text-sm text-muted-foreground">No approved, unexported entries. Approve classified entries first (maker-checker).</div>}
          <div className="panel max-h-[55vh] overflow-auto" data-testid="export-preview-table">
            <table className="w-full min-w-[1000px] text-xs">
              <thead className="glass sticky top-0 text-left text-muted-foreground"><tr>{["Date", "Voucher", "Ledger entries (Dr / Cr)", "Bills", "Amount", "Validation"].map((h) => <th key={h} className="p-2.5 font-medium">{h}</th>)}</tr></thead>
              <tbody>{res.rows.map((r) => (
                <tr key={r.id} className={`border-t align-top ${r.errors.length ? "bg-rose-500/5" : ""}`} data-testid="export-preview-row">
                  <td className="num p-2.5">{fmtDate(r.txn_date)}</td><td className="p-2.5">{r.voucher_type}</td>
                  <td className="p-2.5">{r.entries.map((e, i) => <div key={i} className="flex justify-between gap-4"><span>{e.dr ? "Dr" : "Cr"} {e.ledger}</span><span className="num">{inr(e.dr || e.cr)}</span></div>)}
                    <div className="mt-1 truncate font-mono text-[10px] text-muted-foreground">{r.narration}</div></td>
                  <td className="p-2.5">{r.bills.map((b) => `${b.invoice_no} ${inr(b.amount)}`).join(", ") || "—"}</td>
                  <td className="num p-2.5">{inr(r.amount)}</td>
                  <td className="p-2.5">{r.errors.length ? r.errors.map((e) => <div key={e} className="text-rose-500">✕ {e}</div>) : <span className="text-emerald-500">✓ Ready</span>}
                    {r.warnings.map((w) => <div key={w} className="text-amber-500">! {w}</div>)}</td>
                </tr>))}</tbody>
            </table>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            {res.rows.some((r) => r.errors.some((e) => e.startsWith("Already"))) && <Input className="w-80" placeholder="Re-export reason (only if Tally import failed)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reexport-reason-input" />}
            <Button onClick={exp} disabled={busy || !res.valid || !can("admin", "accountant")} data-testid="tally-export-button">Export {res.valid} valid entries as {format.toUpperCase()}</Button>
            {invalid.length > 0 && <span className="text-xs text-muted-foreground">{invalid.length} blocked entries will not be exported.</span>}
          </div>
        </div>
      )}
      <h3 className="mb-3 font-semibold">Export batches & Tally import status</h3>
      <div className="mb-3 text-xs text-muted-foreground">"Exported" means a file was generated. Only mark "Imported into Tally" after TallyPrime confirms the import. Exported entries are locked to prevent double posting.</div>
      <Batches reload={tick} />
    </div>
  );
}
